import {
  Button,
  Callout,
  type CalmStatus,
  Card,
  EvidenceList,
  FormField,
  Modal,
  PageHeader,
  SectionHeader,
  StatePanel,
  StatusBadge,
  TextInput,
} from "@moneymap/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApi } from "../api/ApiProvider";
import type { CoverageRead } from "../api/types";
import { QueryState } from "../components/QueryState";
import {
  formatHouseholdDate,
  formatInstant,
  formatMoney,
  titleFromMachineValue,
} from "../lib/format";

function coverageStatus(value: string): CalmStatus {
  if (
    value === "covered" ||
    value === "paid" ||
    value === "partially_covered" ||
    value === "at_risk"
  ) {
    return value;
  }
  if (value === "unconfirmed") return "needs_confirmation";
  return "pending";
}

function FreshnessLine({
  asOf,
  isStale,
  timezone = "UTC",
  isUpdating = false,
}: {
  asOf: string;
  isStale: boolean;
  timezone?: string;
  isUpdating?: boolean;
}) {
  return (
    <div className={isStale ? "freshness-line is-stale" : "freshness-line"}>
      <span className="freshness-line__dot" aria-hidden="true" />
      <span>
        {isStale ? "Data stale" : "Current calculation"} · {formatInstant(asOf, timezone)}
      </span>
      {isUpdating ? <StatusBadge status="updating" /> : null}
    </div>
  );
}

function ReservationList({
  items,
  currency,
  emptyMessage,
}: {
  items: Array<{
    id: string;
    label: string;
    dueDate?: string | undefined;
    amountMinor: string;
    state: string;
    confidence: string;
  }>;
  currency: string;
  emptyMessage: string;
}) {
  if (items.length === 0) return <p className="muted small">{emptyMessage}</p>;
  return (
    <div className="card-list">
      {items.map((item) => (
        <div className="list-row" key={item.id}>
          <div className="list-row__main">
            <strong>{item.label}</strong>
            <span>
              {item.dueDate ? formatHouseholdDate(item.dueDate) + " · " : ""}
              {titleFromMachineValue(item.confidence)}
            </span>
          </div>
          <div className="list-row__side">
            <strong>{formatMoney(item.amountMinor, currency)}</strong>
            <StatusBadge status={coverageStatus(item.state)} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function HomePage() {
  const api = useApi();
  const briefingQuery = useQuery({
    queryKey: ["briefing"],
    queryFn: () => api.getBriefing(),
    refetchInterval: 60_000,
  });
  const briefing = briefingQuery.data?.data;
  const acknowledgeMutation = useMutation({
    mutationFn: (briefingId: string) => api.acknowledgeBriefing(briefingId),
  });

  useEffect(() => {
    if (
      !briefing ||
      briefing.acknowledgedAt ||
      acknowledgeMutation.isPending ||
      acknowledgeMutation.isSuccess
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => acknowledgeMutation.mutate(briefing.id), 2_500);
    return () => window.clearTimeout(timer);
  }, [briefing, acknowledgeMutation]);

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Daily briefing"
        title="Here is the household plan"
        description="One current action, the coverage behind it, then you can get back to life."
        actions={
          <Link className="text-link" to="/needs-confirmation">
            Review unknowns
          </Link>
        }
      />
      <QueryState
        isPending={briefingQuery.isPending}
        isError={briefingQuery.isError && !briefing}
        error={briefingQuery.error}
        onRetry={() => void briefingQuery.refetch()}
      >
        {briefing ? (
          <>
            <FreshnessLine
              asOf={briefing.freshness.asOf}
              isStale={briefing.freshness.isStale}
              isUpdating={briefingQuery.isFetching}
            />
            {briefing.freshness.isStale ? (
              <Callout title="This is the last trustworthy plan" tone="warning">
                Data cannot be confirmed as current. IterNest Money has suppressed actions that
                depend on fresh balances.
              </Callout>
            ) : null}
            {briefing.limitations.length > 0 ? (
              <Callout title="What this plan cannot confirm yet" tone="warning">
                <ul>
                  {briefing.limitations.map((limitation) => (
                    <li key={limitation}>{limitation}</li>
                  ))}
                </ul>
              </Callout>
            ) : null}
            <Card className="hero-card">
              <p className="mm-eyebrow">Today’s action</p>
              <h2>{briefing.todayAction.action}</h2>
              <p className="hero-card__reason">{briefing.todayAction.whyNow}</p>
              <p className="hero-card__protects">
                Protects: {briefing.todayAction.protects.join(", ") || "the current covered plan"}
              </p>
              <div className="button-row">
                <Link
                  className="mm-button mm-button--primary"
                  to={"/action/" + briefing.todayAction.id}
                >
                  {briefing.todayAction.type === "do_nothing"
                    ? "View today’s plan"
                    : "Review this action"}
                </Link>
                <Link
                  className="mm-button mm-button--quiet"
                  to={"/action/" + briefing.todayAction.id + "?correct=1"}
                >
                  Correct this
                </Link>
              </div>
            </Card>
            <Card>
              <SectionHeader
                title="What is covered"
                description="Through the active continuous horizon."
              />
              <Link className="safe-to-use-link" to="/coverage">
                <span>
                  Safe to use
                  <small>
                    after known obligations, protected purposes, buffers, and risk holds
                  </small>
                </span>
                <strong className="amount-primary">
                  {formatMoney(briefing.displaySafeToUseMinor, briefing.currency)}
                </strong>
              </Link>
              <ReservationList
                items={briefing.whatIsCovered}
                currency={briefing.currency}
                emptyMessage="No covered items were returned for this horizon."
              />
            </Card>
            {briefing.whatChanged.length > 0 ? (
              <Card>
                <SectionHeader
                  title="What changed"
                  description="Since the briefing you last acknowledged."
                />
                <ul className="change-list">
                  {briefing.whatChanged.map((change) => (
                    <li
                      key={change.key}
                      className={"change-list__item change-list__item--" + change.severity}
                    >
                      {change.message}
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
            {briefing.whatGotPaid.length > 0 ? (
              <Card>
                <SectionHeader title="What got paid" />
                <ReservationList
                  items={briefing.whatGotPaid}
                  currency={briefing.currency}
                  emptyMessage=""
                />
              </Card>
            ) : null}
            {briefing.whatIsComing.length > 0 ? (
              <Card>
                <SectionHeader
                  title="What is coming"
                  description="Known obligations and their current confidence."
                />
                <ReservationList
                  items={briefing.whatIsComing}
                  currency={briefing.currency}
                  emptyMessage=""
                />
              </Card>
            ) : null}
            {briefing.spark.status !== "not_configured" ? (
              <Card>
                <SectionHeader
                  title="Spark"
                  description={
                    briefing.spark.status === "done_required"
                      ? "You are done for the required goal."
                      : "Current variable-income pressure."
                  }
                  action={
                    <Link className="text-link" to="/spark">
                      Open Spark
                    </Link>
                  }
                />
                <div className="summary-grid">
                  <div className="summary-tile">
                    <span>Confirmed earnings</span>
                    <strong>{formatMoney(briefing.spark.confirmedMinor, briefing.currency)}</strong>
                  </div>
                  <div className="summary-tile">
                    <span>Required goal</span>
                    <strong>
                      {formatMoney(briefing.spark.minimumTargetMinor, briefing.currency)}
                    </strong>
                  </div>
                  <div className="summary-tile">
                    <span>Workable days</span>
                    <strong>{briefing.spark.workableDays}</strong>
                  </div>
                </div>
                {briefing.spark.status === "planning_problem" ? (
                  <Callout title="The current Spark plan needs an adjustment" tone="warning">
                    There are no workable days left for the remaining required amount. Review the
                    plan rather than relying on an impossible daily average.
                  </Callout>
                ) : null}
              </Card>
            ) : null}
            <Card>
              <SectionHeader
                title="Household"
                description="Protected purposes stay ahead of general spending."
                action={
                  <Link className="text-link" to="/plan">
                    Open the plan
                  </Link>
                }
              />
              {briefing.whatIsCovered.length > 0 ? (
                <p className="muted">
                  Your purpose coverage is shown above. Open Plan to inspect groceries, family
                  commitments, and their funding windows.
                </p>
              ) : (
                <p className="muted">
                  Add or confirm a household purpose to include it in this briefing.
                </p>
              )}
            </Card>
            {briefing.needsAttention.length > 0 ? (
              <Card>
                <SectionHeader
                  title="Needs your attention"
                  description="Only items that can materially change the plan."
                  action={
                    <Link className="text-link" to="/needs-confirmation">
                      Review all
                    </Link>
                  }
                />
                <div className="card-list">
                  {briefing.needsAttention.map((item) => (
                    <Link className="list-row" to="/needs-confirmation" key={item.id}>
                      <div className="list-row__main">
                        <strong>{item.message}</strong>
                        <span>{titleFromMachineValue(item.type)}</span>
                      </div>
                      <StatusBadge status="needs_confirmation" />
                    </Link>
                  ))}
                </div>
              </Card>
            ) : null}
          </>
        ) : null}
      </QueryState>
    </div>
  );
}

export function ActionDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<"deferred" | "rejected" | "already_handled" | null>(
    searchParams.get("correct") ? "rejected" : null,
  );
  const [reason, setReason] = useState("");
  const query = useQuery({
    queryKey: ["recommendation", id],
    queryFn: () => api.getRecommendation(id),
  });
  const mutation = useMutation({
    mutationFn: (input: {
      outcome: "completed" | "deferred" | "rejected" | "already_handled";
      reason?: string;
    }) =>
      api.recordRecommendationOutcome(id, {
        outcome: input.outcome,
        ...(input.reason ? { reason: input.reason } : {}),
        ...(input.outcome === "deferred"
          ? { deferUntil: new Date(Date.now() + 86_400_000).toISOString() }
          : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      navigate("/home", { replace: true, state: { planUpdated: true } });
    },
  });
  const recommendation = query.data?.data;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Recommendation"
        title="Why this is the next move"
        description="IterNest Money recommends and remembers your decision. It never moves money or initiates a payment."
      />
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {recommendation ? (
          <>
            <Card className="hero-card">
              <p className="mm-eyebrow">{titleFromMachineValue(recommendation.priority)}</p>
              <h2>{recommendation.action}</h2>
              <p className="hero-card__reason">{recommendation.whyNow}</p>
              <p className="hero-card__protects">
                Protects: {recommendation.protects.join(", ") || "the current household plan"}
              </p>
            </Card>
            <Card>
              <SectionHeader title="Why now" />
              <p>{recommendation.reason}</p>
              <EvidenceList items={recommendation.evidence} />
            </Card>
            {mutation.error ? (
              <Callout title="Your outcome was not recorded" tone="urgent" role="alert">
                {mutation.error.message}
              </Callout>
            ) : null}
            <div className="button-row">
              <Button
                type="button"
                onClick={() => mutation.mutate({ outcome: "completed" })}
                isLoading={mutation.isPending}
              >
                {recommendation.type === "do_nothing" ? "Acknowledge plan" : "Mark done"}
              </Button>
              <Button variant="secondary" type="button" onClick={() => setOutcome("deferred")}>
                Not now
              </Button>
              <Button variant="quiet" type="button" onClick={() => setOutcome("already_handled")}>
                Already handled
              </Button>
              <Button variant="quiet" type="button" onClick={() => setOutcome("rejected")}>
                Correct this
              </Button>
            </div>
          </>
        ) : null}
      </QueryState>
      <Modal
        isOpen={outcome !== null}
        title={
          outcome === "deferred"
            ? "Set this aside for now"
            : outcome === "already_handled"
              ? "Record that this is handled"
              : "What needs correcting?"
        }
        description="IterNest Money records this outcome so an unchanged recommendation is not repeated."
        onClose={() => setOutcome(null)}
        footer={
          <>
            <Button variant="quiet" type="button" onClick={() => setOutcome(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={mutation.isPending}
              onClick={() => {
                if (outcome) mutation.mutate({ outcome, ...(reason ? { reason } : {}) });
              }}
            >
              Save outcome
            </Button>
          </>
        }
      >
        <FormField
          label={outcome === "rejected" ? "Correction or reason" : "Optional note"}
          htmlFor="outcome-reason"
          hint={
            outcome === "rejected"
              ? "Name the assumption that is wrong. You can correct source data from Activity."
              : undefined
          }
        >
          <textarea
            id="outcome-reason"
            className="mm-input"
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        </FormField>
        {outcome === "rejected" ? (
          <Link className="text-link" to="/activity">
            Correct a transaction in Activity
          </Link>
        ) : null}
      </Modal>
    </div>
  );
}

const horizonOptions = [
  { value: "default", label: "Default" },
  { value: "7d", label: "7 days" },
  { value: "next_paycheck", label: "Next paycheck" },
  { value: "30d", label: "30 days" },
  { value: "custom", label: "Custom" },
] as const;

export function CoveragePage() {
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const horizon = searchParams.get("horizon") ?? "default";
  const customEndDate = searchParams.get("end") ?? "";
  const query = useQuery({
    queryKey: ["coverage", horizon, customEndDate],
    queryFn: () => api.getCoverage(horizon, customEndDate || undefined),
  });
  const coverage = query.data?.data;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Coverage evidence"
        title="What the money is protecting"
        description="Safe to use is what remains after eligible funds, reservations, protected purposes, buffers, and high-risk holds."
      />
      <div className="segmented horizon-selector" role="group" aria-label="Coverage horizon">
        {horizonOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={horizon === option.value}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.set("horizon", option.value);
              if (option.value !== "custom") next.delete("end");
              setSearchParams(next);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
      {horizon === "custom" ? (
        <FormField label="Custom horizon end" htmlFor="custom-horizon">
          <TextInput
            id="custom-horizon"
            type="date"
            value={customEndDate}
            onChange={(event) => {
              const next = new URLSearchParams(searchParams);
              next.set("horizon", "custom");
              next.set("end", event.currentTarget.value);
              setSearchParams(next);
            }}
          />
        </FormField>
      ) : null}
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {coverage ? <CoverageContent coverage={coverage} isUpdating={query.isFetching} /> : null}
      </QueryState>
    </div>
  );
}

function CoverageContent({
  coverage,
  isUpdating,
}: {
  coverage: CoverageRead;
  isUpdating: boolean;
}) {
  return (
    <>
      <FreshnessLine
        asOf={coverage.calculatedAt}
        isStale={coverage.unknowns.length > 0}
        isUpdating={isUpdating}
      />
      <Card className="coverage-total-card">
        <span className="muted small">
          Safe to use through {formatHouseholdDate(coverage.horizonEndDate)}
        </span>
        <strong className="amount-primary">
          {formatMoney(coverage.displaySafeToUseMinor, coverage.currency)}
        </strong>
        {coverage.signedSafeToUseMinor.startsWith("-") ? (
          <Callout title="The plan has a shortfall" tone="urgent">
            The signed result is {formatMoney(coverage.signedSafeToUseMinor, coverage.currency)}.
            Safe to use is floored at zero rather than hiding the shortfall.
          </Callout>
        ) : null}
        <div className="summary-grid">
          <div className="summary-tile">
            <span>Eligible current funds</span>
            <strong>{formatMoney(coverage.eligibleFundsMinor, coverage.currency)}</strong>
          </div>
          <div className="summary-tile">
            <span>Horizon</span>
            <strong>
              {formatHouseholdDate(coverage.horizonStartDate)} –{" "}
              {formatHouseholdDate(coverage.horizonEndDate)}
            </strong>
          </div>
          <div className="summary-tile">
            <span>Engine</span>
            <strong>{coverage.engineVersion}</strong>
          </div>
        </div>
      </Card>
      {coverage.unknowns.length > 0 ? (
        <Callout title="Uncertainty in this horizon" tone="warning">
          <ul>
            {coverage.unknowns.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      <Card>
        <SectionHeader
          title="Reservations"
          description="A reservation is a plan; it does not prove money moved."
        />
        {coverage.reservations.length === 0 ? (
          <StatePanel title="No reservations in this horizon">
            Add or confirm obligations and protected purposes to build the coverage picture.
          </StatePanel>
        ) : (
          <div className="card-list">
            {coverage.reservations.map((reservation) => (
              <div className="list-row" key={reservation.id}>
                <div className="list-row__main">
                  <strong>{reservation.label}</strong>
                  <span>
                    {reservation.purposeName ? reservation.purposeName + " · " : ""}
                    {reservation.dueDate
                      ? formatHouseholdDate(reservation.dueDate)
                      : "Protected commitment"}
                    {reservation.nextFundingSource
                      ? " · Next: " + reservation.nextFundingSource
                      : ""}
                  </span>
                </div>
                <div className="list-row__side">
                  <strong>{formatMoney(reservation.reservedMinor, coverage.currency)}</strong>
                  <StatusBadge status={coverageStatus(reservation.state)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card>
        <SectionHeader
          title="Buffers"
          description="Minimum floors that remain unavailable to routine recommendations."
        />
        {coverage.buffers.length === 0 ? (
          <p className="muted">No active buffer rules were returned.</p>
        ) : (
          <div className="card-list">
            {coverage.buffers.map((buffer) => (
              <div className="list-row" key={buffer.id}>
                <div className="list-row__main">
                  <strong>{buffer.label}</strong>
                </div>
                <div className="list-row__side">
                  <strong>{formatMoney(buffer.amountMinor, coverage.currency)}</strong>
                  <StatusBadge status={coverageStatus(buffer.state)} />
                </div>
              </div>
            ))}
          </div>
        )}
        <EvidenceList items={coverage.evidence} />
      </Card>
    </>
  );
}
